import { useState } from "react";
import MatchCards from "@/components/KbvMatchCards";
import { useFilterOdds } from "@/hooks/useFilterOdds";
import { useLiveDataForMatch } from "@/hooks/useLiveData";
import { useMatchData } from "@/hooks/useMatchData";
import { useMatchOdds } from "@/hooks/useMatchOdds";
import {
  LIVE_STATUSES,
  FINISHED_STATUSES,
  NOT_STARTED_STATUS,
} from "@/contants";
import MatchMinute from "@/components/KbvMatchMinute";
import OddsStatus from "@/components/LichThiDauFull/OddsStatus";
import { flattenMatchOdds } from "@/components/LichThiDauFull/slug";
import { formatDateTime } from "@/utils/format";
import ICON_CORNER from "@/assets/images/corner.svg";

const oddsPriceClass = (trend) => {
  const t = String(trend || "");
  if (t === "up" || t === "increased") return "text-green-600";
  if (t === "down" || t === "decreased") return "text-red-600";
  return "";
};

function TeamLine({ logo, name, isWin, yellow, red, direction, score, showScore, halfScore }) {
  const [broken, setBroken] = useState(false);
  const showLogo = Boolean(logo) && !broken;

  return (
    <div className="kbv-ltd-sb-team">
      <span className={`kbv-ltd-sb-team-logo ${showLogo ? "" : "is-fallback"}`}>
        {showLogo ? (
          <img src={logo} alt="" onError={() => setBroken(true)} />
        ) : (
          <span>{String(name || "?").trim().charAt(0).toUpperCase() || "?"}</span>
        )}
      </span>
      <span className={`kbv-ltd-sb-team-name ${isWin ? "is-win" : ""}`} title={name}>
        {name}
      </span>
      <MatchCards yellow={yellow} red={red} direction={direction} />
      {showScore ? (
        <span className={`kbv-ltd-sb-score ${isWin ? "is-win" : ""}`}>
          {score}
          {halfScore != null ? <span className="kbv-ltd-sb-half">({halfScore})</span> : null}
        </span>
      ) : null}
    </div>
  );
}

function StatusBlock({
  isLive,
  isFinished,
  isNotStarted,
  matchTime,
  liveScore,
  status,
  halfStartTime,
  homeCorner,
  awayCorner,
}) {
  const corners =
    !isNotStarted ? (
      <span className="kbv-ltd-sb-corners">
        <img src={ICON_CORNER} alt="" className="kbv-corner-icon" />
        {homeCorner}:{awayCorner}
      </span>
    ) : null;

  if (isFinished) {
    return (
      <span className="kbv-ltd-sb-status">
        <span>KT</span>
        {corners}
      </span>
    );
  }
  if (isLive) {
    return (
      <span className="kbv-ltd-sb-status is-live">
        <MatchMinute
          liveScore={liveScore}
          status={status}
          matchTime={matchTime}
          halfStartTime={halfStartTime}
          className="kbv-ltd-sb-minute"
        />
        {corners}
      </span>
    );
  }
  if (isNotStarted && matchTime) {
    const { date, time } = formatDateTime(matchTime);
    return (
      <span className="kbv-ltd-sb-status">
        <span className="kbv-ltd-sb-time">{time}</span>
        <span className="kbv-ltd-sb-date">{date}</span>
      </span>
    );
  }
  return <span className="kbv-ltd-sb-status">—</span>;
}

function OddsRow({ handicapText, trend, price }) {
  return (
    <div className="kbv-ltd-sb-odds-row">
      {handicapText ? <span className="kbv-ltd-sb-hdp">{handicapText}</span> : null}
      <span className={`kbv-ltd-sb-odd ${oddsPriceClass(trend)}`}>
        <OddsStatus trend={trend} />
        {price}
      </span>
    </div>
  );
}

function OddsLine({
  homeHandicapText,
  awayHandicapText,
  homeOdds,
  awayOdds,
  homeTrend,
  awayTrend,
}) {
  return (
    <div className="kbv-ltd-sb-odds">
      <OddsRow handicapText={homeHandicapText} trend={homeTrend} price={homeOdds} />
      <OddsRow handicapText={awayHandicapText} trend={awayTrend} price={awayOdds} />
    </div>
  );
}

export default function SidebarMatch({ match: rawMatch, isLastItem = false }) {
  const match = flattenMatchOdds(rawMatch);
  const { getScore, getOdds } = useLiveDataForMatch(match.id);
  const liveScore = getScore();
  const liveOdds = getOdds();
  const filterOdds = useFilterOdds(match, liveOdds, null, -1);
  const odds = useMatchOdds(match, filterOdds, -1);

  const handicapValue = odds.handicapValue;
  const n = Number(handicapValue);
  const isAwayHandicap = !Number.isNaN(n) && n < 0;
  const abs = Number.isNaN(n) ? null : Math.abs(n);
  const homeHandicapText = abs == null ? "-" : !isAwayHandicap ? `-${abs}` : null;
  const awayHandicapText = abs == null ? "-" : isAwayHandicap ? `-${abs}` : null;

  const statusNum =
    liveScore?.status !== undefined ? Number(liveScore.status) : Number(match.status);
  const isLive = LIVE_STATUSES.includes(statusNum);
  const isFinished = FINISHED_STATUSES.includes(statusNum);
  const isNotStarted = NOT_STARTED_STATUS.includes(statusNum);

  const {
    currentHomeScore,
    currentAwayScore,
    currentHomeHalfScore,
    currentAwayHalfScore,
    isHomeWin,
    isAwayWin,
    homeYellow,
    homeRed,
    awayYellow,
    awayRed,
    currentHomeCorner,
    currentAwayCorner,
  } = useMatchData(liveScore, match);

  const homeName = match.homeTeam?.nameVi || match.homeTeam?.name || match.homeName || "";
  const awayName = match.awayTeam?.nameVi || match.awayTeam?.name || match.awayName || "";
  const showScore = !isNotStarted;

  return (
    <div className={`kbv-ltd-sb-match ${isLastItem ? "" : "has-sep"}`}>
      <StatusBlock
        isLive={isLive}
        isFinished={isFinished}
        isNotStarted={isNotStarted}
        matchTime={match.matchTime}
        liveScore={liveScore}
        status={statusNum}
        halfStartTime={match.halfStartTime}
        homeCorner={currentHomeCorner}
        awayCorner={currentAwayCorner}
      />
      <div className="kbv-ltd-sb-teams">
        <TeamLine
          logo={match.homeTeam?.logo}
          name={homeName}
          isWin={isHomeWin}
          yellow={homeYellow}
          red={homeRed}
          direction="home"
          score={currentHomeScore}
          showScore={showScore}
          halfScore={isNotStarted ? null : currentHomeHalfScore}
        />
        <TeamLine
          logo={match.awayTeam?.logo}
          name={awayName}
          isWin={isAwayWin}
          yellow={awayYellow}
          red={awayRed}
          direction="away"
          score={currentAwayScore}
          showScore={showScore}
          halfScore={isNotStarted ? null : currentAwayHalfScore}
        />
      </div>
      {!isFinished ? (
        <OddsLine
          homeHandicapText={homeHandicapText}
          awayHandicapText={awayHandicapText}
          homeOdds={odds.homeHandicapOdds}
          awayOdds={odds.awayHandicapOdds}
          homeTrend={odds.homeHandicapTrend}
          awayTrend={odds.awayHandicapTrend}
        />
      ) : null}
    </div>
  );
}
